<template>
  <d-dialog
    v-if="isOpenVersionHistory"
    :showFooter="false"
    @close="closeVersionHistory"
    width="850px"
    class="versionHistoryPopup"
  >
    <div slot="header" class="card__title">
      <d-icon-version-history-title />
      {{ $t("editor.versionHistory") }}
    </div>
    <d-loading-block v-if="isOpenLoading">
      <template slot="title">
        {{ $t("player.loading") }}
      </template>
    </d-loading-block>

    <d-empty-block v-if="isVersionHistoryDataEmpty">
      <template slot="icon">
        <d-icon-empty-view />
      </template>
      <template slot="title">{{ $t("editor.emptyTitle") }} </template>
      <template slot="description">{{ $t("editor.emptyContent") }}</template>
    </d-empty-block>

    <template v-else>
      <history-action v-for="history in versionHistoryData" :key="history.id" :history-data="history" />

      <button
        v-if="previousVersionHistoryData.length > 0"
        class="dh-btn btn-action btn-md versionHistoryPopup__previousButton"
        @click="togglePreviousHistory"
      >
        <span>{{ $t("versionHistory.previousContext") }}</span>
        <font-awesome-icon :icon="showPreviousHistory ? ['fas', 'chevron-up'] : ['fas', 'chevron-down']" />
      </button>
      <template v-if="showPreviousHistory">
        <history-action v-for="history in previousVersionHistoryData" :key="history.id" :history-data="history" />
      </template>
    </template>
  </d-dialog>
</template>

<script>
import { mapActions, mapState } from "vuex";

import { fetchVersionHistoryByWorkflowId } from "@/server/version-history";
import { loggingError } from "@/server/error-log-server.js";
import DIconVersionHistoryTitle from "@/components/icons/editor/DIconVersionHistoryTitle.vue";
import DIconEmptyView from "@/components/icons/editor/DIconEmptyView.vue";
import { HISTORY_ACTION_TYPE } from "@/constants/versionHistory";

export default {
  name: "VersionHistoryPopup",
  components: {
    DIconEmptyView,
    DIconVersionHistoryTitle,
    HistoryAction: () => import("@/components/DEditorBuilder/VersionHistory/Fragments/HistoryAction.vue"),
  },
  data() {
    return {
      versionHistoryData: [],
      previousVersionHistoryData: [],
      isOpenLoading: false,

      showPreviousHistory: false,
    };
  },
  computed: {
    ...mapState("versionHistory", ["getLatestOnly"]),
    isVersionHistoryDataEmpty() {
      return !this.isOpenLoading && this.versionHistoryData.length === 0;
    },
    isOpenVersionHistory() {
      return this.$store.state.versionHistory.isOpenVersionHistory;
    },
    workflowId() {
      return this.$store.state.versionHistory.workflowId;
    },
  },
  methods: {
    ...mapActions("versionHistory", ["openVersionHistory", "closeVersionHistory"]),
    clearVersionHistoryData() {
      this.versionHistoryData = [];
      this.previousVersionHistoryData = [];
      this.showPreviousHistory = false;
    },

    togglePreviousHistory() {
      this.showPreviousHistory = !this.showPreviousHistory;
    },
    async refreshVersionHistoryData(workflowId) {
      try {
        this.isOpenLoading = true;
        if (!workflowId) return;

        const [versionHistory, previousVersionHistory] = await Promise.all([
          fetchVersionHistoryByWorkflowId({
            workflowId,
            lastest: this.getLatestOnly,
          }),
          fetchVersionHistoryByWorkflowId({
            workflowId,
            previous: true,
          }),
        ]);
        const { ok: latestOk, data: latestData, errorMessage: latestErrorMessage } = versionHistory;
        const { ok: previousOk, data: previousData, errorMessage: previousErrorMessage } = previousVersionHistory;
        if (!latestOk || !previousOk) throw new Error(latestErrorMessage || previousErrorMessage);

        this.versionHistoryData = latestData.items;

        /** For previous context, only display the immediate parent's history */
        const latestDuplicationIndex = previousData.items.findIndex(
          (item) => item.actionType === HISTORY_ACTION_TYPE.DUPLICATION_TARGET
        );
        this.previousVersionHistoryData =
          latestDuplicationIndex === -1 ? previousData.items : previousData.items.slice(0, latestDuplicationIndex + 1);
      } catch (error) {
        console.error({ error });
        loggingError(error);
        this.clearVersionHistoryData();
      } finally {
        this.isOpenLoading = false;
      }
    },
  },
  watch: {
    workflowId: {
      immediate: true,
      handler(workflowId) {
        if (!this.isOpenVersionHistory) return;
        this.clearVersionHistoryData();
        this.refreshVersionHistoryData(workflowId);
      },
    },
    isOpenVersionHistory(isOpenVersionHistory) {
      if (!isOpenVersionHistory || !this.workflowId) {
        this.clearVersionHistoryData();
        return;
      }
      this.refreshVersionHistoryData(this.workflowId);
    },
  },
};
</script>

<style scoped lang="scss">
.versionHistoryPopup {
  &__previousButton {
    align-self: center;
    margin: 32px 0px;
  }
}
</style>
